import { IconPencil, IconPlus } from '@tabler/icons-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Banner,
  Chip,
  Dropdown,
  type DropdownOption,
  Sheet,
  Spinner,
  useToast,
} from '@/components/common';
import { useResource } from '@/hooks';
import { errorMessage } from '@/lib/api-client';
import type { Role, User } from '@/types/api';
import { ROLES, useAuth } from '../../auth';
import { useDoctor } from '../../doctors';
import { settingsApi, type UserPatch } from '../api';

function useRoleOptions(): DropdownOption<Role>[] {
  const { t } = useTranslation();
  return ROLES.map((r) => ({ value: r, label: t(`common.role.${r}`) }));
}

/** Toggle chips for the doctors a nurse works for. */
function DoctorPicker({
  doctors,
  value,
  onChange,
}: {
  doctors: User[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="field">
      <span className="field__label">{t('settings.users.doctors')}</span>
      {doctors.length ? (
        <div className="chips">
          {doctors.map((d) => (
            <Chip
              key={d.id}
              pressed={value.includes(d.id)}
              onClick={() =>
                onChange(value.includes(d.id) ? value.filter((x) => x !== d.id) : [...value, d.id])
              }
            >
              {d.name}
            </Chip>
          ))}
        </div>
      ) : (
        <span className="small muted">{t('settings.users.noDoctors')}</span>
      )}
      <span className="small muted">{t('settings.users.doctorsHint')}</span>
    </div>
  );
}

type FormValues = {
  name: string;
  email: string;
  password: string;
  role: Role;
  /** Only an admin's is editable: a dentist always treats patients, a nurse never. */
  treatsPatients: boolean;
  active: boolean;
  doctorIds: string[];
};

/** New user (`user` undefined) or edit: the e-mail is fixed once created; a password resets it. */
function UserForm({
  user,
  doctors,
  onSaved,
}: {
  user?: User;
  doctors: User[];
  onSaved: (u: User) => void;
}) {
  const { t } = useTranslation();
  const roleOptions = useRoleOptions();
  const toast = useToast();
  const { user: me } = useAuth();
  const [v, setV] = useState<FormValues>({
    name: user?.name ?? '',
    email: user?.email ?? '',
    password: '',
    role: user?.role ?? 'DENTIST',
    treatsPatients: user?.treatsPatients ?? false,
    active: user?.active ?? true,
    doctorIds: user?.doctorIds ?? [],
  });
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<FormValues>) => setV((x) => ({ ...x, ...patch }));
  const nurse = v.role === 'ASSISTANT';
  const admin = v.role === 'ADMIN';
  const treatsPatients = admin ? v.treatsPatients : v.role === 'DENTIST';
  const self = user?.id === me?.id;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const doctorIds = nurse ? v.doctorIds : undefined;
      if (user) {
        const patch: UserPatch = { name: v.name.trim(), role: v.role, treatsPatients, doctorIds };
        if (!self) patch.active = v.active;
        if (v.password) patch.password = v.password;
        onSaved(await settingsApi.updateUser(user.id, patch));
      } else {
        onSaved(
          await settingsApi.createUser({
            name: v.name.trim(),
            email: v.email.trim(),
            password: v.password,
            role: v.role,
            treatsPatients,
            doctorIds,
          })
        );
      }
    } catch (err) {
      toast.show(errorMessage(err, { CONFLICT: t('settings.users.exists') }), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit}>
      <div className="field">
        <label htmlFor="u-name">{t('settings.users.name')}</label>
        <input
          id="u-name"
          className="input"
          required
          data-autofocus={!user || undefined}
          value={v.name}
          onChange={(e) => set({ name: e.target.value })}
        />
      </div>
      <div className="field">
        <label htmlFor="u-email">{t('settings.users.email')}</label>
        <input
          id="u-email"
          className="input"
          type="email"
          required
          disabled={!!user}
          value={v.email}
          onChange={(e) => set({ email: e.target.value })}
        />
      </div>
      {/* Support mode never sets a password (the server refuses it too). */}
      {!me?.support && (
        <div className="field">
          <label htmlFor="u-pass">
            {t(user ? 'settings.users.newPassword' : 'settings.users.initialPassword')}
          </label>
          <input
            id="u-pass"
            className="input"
            type="password"
            minLength={8}
            required={!user}
            autoComplete="new-password"
            value={v.password}
            onChange={(e) => set({ password: e.target.value })}
          />
        </div>
      )}
      <div className="field">
        <label htmlFor="u-role">{t('settings.users.role')}</label>
        <Dropdown
          id="u-role"
          value={v.role}
          options={roleOptions}
          onChange={(role) => set({ role })}
        />
      </div>
      {admin && (
        <label className="check">
          <input
            type="checkbox"
            checked={v.treatsPatients}
            onChange={(e) => set({ treatsPatients: e.target.checked })}
          />
          {t('settings.users.treatsPatients')}
        </label>
      )}
      {nurse && (
        <DoctorPicker
          doctors={doctors}
          value={v.doctorIds}
          onChange={(doctorIds) => set({ doctorIds })}
        />
      )}
      {user && (
        <label className="check">
          <input
            type="checkbox"
            checked={v.active}
            disabled={self}
            onChange={(e) => set({ active: e.target.checked })}
          />
          {t('settings.users.active')}
        </label>
      )}
      <button type="submit" className="btn btn--dark btn--block" disabled={busy}>
        {t(user ? 'common.save' : 'settings.users.create')}
      </button>
    </form>
  );
}

/** The clinic's users. Roles are labels only; a nurse is linked to her doctors here. */
export function UsersPanel() {
  const { t } = useTranslation();
  const users = useResource(() => settingsApi.users(), 'users');
  const { refresh: refreshDoctors } = useDoctor();
  const { user: me } = useAuth();
  const [editing, setEditing] = useState<User | 'new' | null>(null);

  if (users.error) return <Banner tone="error">{errorMessage(users.error)}</Banner>;
  if (!users.data) return <Spinner />;
  const list = users.data;
  const doctors = list.filter((u) => u.treatsPatients && u.active);
  const nameOf = (id: string) => list.find((u) => u.id === id)?.name ?? '—';

  const saved = (u: User) => {
    users.setData((all) =>
      all?.some((x) => x.id === u.id)
        ? all.map((x) => (x.id === u.id ? u : x))
        : [...(all ?? []), u]
    );
    setEditing(null);
    refreshDoctors();
  };

  return (
    <section className="card">
      <div className="card__head">
        <h2>{t('settings.users.title')}</h2>
        {/* New users get a password: the clinic's admin adds them, not support mode. */}
        {!me?.support && (
          <button type="button" className="btn btn--sm btn--dark" onClick={() => setEditing('new')}>
            <IconPlus size={18} /> {t('common.new')}
          </button>
        )}
      </div>
      <ul className="list">
        {list.map((u) => (
          <li key={u.id} className="user-row">
            <div className="grow">
              <strong>{u.name}</strong>
              {!u.active && <span className="badge">{t('settings.users.inactive')}</span>}
              <div className="small muted">
                {t(`common.role.${u.role}`)}
                {u.role === 'ADMIN' && u.treatsPatients && ` + ${t('common.role.DENTIST')}`} ·{' '}
                {u.email}
              </div>
              {u.role === 'ASSISTANT' && u.doctorIds.length > 0 && (
                <div className="small muted">
                  {t('settings.users.worksFor', { names: u.doctorIds.map(nameOf).join(', ') })}
                </div>
              )}
            </div>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setEditing(u)}
              aria-label={t('settings.users.editOf', { name: u.name })}
            >
              <IconPencil size={20} />
            </button>
          </li>
        ))}
      </ul>
      {editing && (
        <Sheet
          title={editing === 'new' ? t('settings.users.newTitle') : editing.name}
          onClose={() => setEditing(null)}
        >
          <UserForm
            user={editing === 'new' ? undefined : editing}
            doctors={doctors}
            onSaved={saved}
          />
        </Sheet>
      )}
    </section>
  );
}
